import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Files, UploadSimple, Trash, DownloadSimple, FilePdf } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = { form16: "Form 16", "26as": "Form 26AS", ais: "AIS", computation: "Computation", other: "Other" };
const AY = ["2026-27", "2025-26", "2024-25", "2023-24"];

export default function ITDocuments() {
  const [clients, setClients] = useState([]);
  const [docs, setDocs] = useState([]);
  const [clientId, setClientId] = useState("");
  const [docType, setDocType] = useState("form16");
  const [ay, setAy] = useState("2026-27");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { axios.get(`${API}/it/clients`).then((r) => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => { fetchDocs(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDocs = async () => {
    try {
      const r = await axios.get(`${API}/it/documents`, { params: clientId ? { client_id: clientId } : {} });
      setDocs(r.data);
    } catch (e) { /* noop */ }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!clientId) { setError("Select a client first"); e.target.value = ""; return; }
    setUploading(true); setError(""); setMsg("");
    try {
      const fd = new FormData(); fd.append("file", f);
      await axios.post(`${API}/it/documents`, fd, { params: { client_id: clientId, doc_type: docType, assessment_year: ay } });
      setMsg(`✓ ${f.name} uploaded (${TYPES[docType]} · AY ${ay})`);
      fetchDocs();
    } catch (err) { setError(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const download = async (d) => {
    const r = await axios.get(`${API}/it/documents/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = d.filename; a.click(); URL.revokeObjectURL(url);
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.filename}?`)) return;
    await axios.delete(`${API}/it/documents/${d.id}`); fetchDocs();
  };

  const years = [...new Set(docs.map((d) => d.assessment_year))].sort().reverse();

  return (
    <div className="p-4 sm:p-8" data-testid="it-documents-page">
      <div className="flex items-center gap-3 mb-6">
        <Files size={30} weight="duotone" className="text-emerald-800" />
        <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Document Vault</h1>
      </div>

      {/* Upload bar */}
      <div className="bg-white border-2 border-dashed border-emerald-300 rounded-sm p-4 mb-6 grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
        <div className="col-span-2">
          <Label className="text-[11px]">Client *</Label>
          <select data-testid="doc-client" value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
            <option value="">— All clients / select to upload —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.pan})</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[11px]">Document Type</Label>
          <select data-testid="doc-type" value={docType} onChange={(e) => setDocType(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
            {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[11px]">Assessment Year</Label>
          <select data-testid="doc-ay" value={ay} onChange={(e) => setAy(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
            {AY.map((a) => <option key={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".pdf,.json,.xlsx,.csv,.jpg,.png,.zip,.txt" className="hidden" onChange={upload} data-testid="doc-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading || !clientId} data-testid="doc-upload-btn" className="w-full bg-emerald-800 hover:bg-emerald-900 text-white">
            <UploadSimple size={16} className="mr-1" /> {uploading ? "Uploading…" : "Upload"}
          </Button>
        </div>
      </div>

      {msg && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs p-3 rounded-sm mb-4" data-testid="doc-msg">{msg}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-sm mb-4" data-testid="doc-error">{error}</div>}

      {/* Year-wise groups */}
      {years.length === 0 && <p className="text-sm text-slate-400 text-center py-10">No documents — upload above (Form-16, 26AS, AIS…)</p>}
      {years.map((y) => (
        <div key={y} className="mb-6" data-testid={`doc-year-${y}`}>
          <h2 className="text-sm font-black text-slate-900 bg-amber-100 inline-block px-3 py-1 rounded-sm mb-2">AY {y}</h2>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto sticky-table">
            <table className="w-full text-sm">
              <thead className="sticky-thead bg-slate-100 text-slate-600 uppercase text-[11px] font-bold">
                <tr>
                  <th className="px-4 py-2.5 text-left">File</th>
                  <th className="px-4 py-2.5 text-left">Type</th>
                  <th className="px-4 py-2.5 text-left">Client</th>
                  <th className="px-4 py-2.5 text-right">Size</th>
                  <th className="px-4 py-2.5 text-left">Uploaded</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {docs.filter((d) => d.assessment_year === y).map((d) => (
                  <tr key={d.id} className="border-b border-slate-100" data-testid={`doc-row-${d.id}`}>
                    <td className="px-4 py-2.5 font-semibold text-slate-900"><FilePdf size={15} className="inline mr-1 text-red-500" />{d.filename}</td>
                    <td className="px-4 py-2.5"><span className="text-[10px] font-bold bg-emerald-50 text-emerald-800 px-2 py-1 rounded-sm uppercase">{TYPES[d.doc_type] || d.doc_type}</span></td>
                    <td className="px-4 py-2.5 text-xs">{d.client_name}<span className="text-slate-400 font-mono ml-1">{d.client_pan}</span></td>
                    <td className="px-4 py-2.5 text-right text-xs font-mono">{(d.size / 1024).toFixed(0)} KB</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{(d.uploaded_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => download(d)} className="text-emerald-700 hover:text-emerald-900 mr-3" data-testid={`doc-dl-${d.id}`}><DownloadSimple size={17} /></button>
                      <button onClick={() => remove(d)} className="text-red-400 hover:text-red-700" data-testid={`doc-del-${d.id}`}><Trash size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
